import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { cn } from "lib/utils";

export default function AppPagination({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  totalItems = 0,
  pageSize = 10,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50],
  className = "",
}) {
  const [goToValue, setGoToValue] = useState(String(currentPage));

  useEffect(() => {
    setGoToValue(String(currentPage));
  }, [currentPage]);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    return Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);
  }, [currentPage, totalPages]);

  const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  const handleGoToSubmit = () => {
    const parsed = Number.parseInt(goToValue, 10);
    if (Number.isNaN(parsed)) return;
    const safePage = Math.min(totalPages, Math.max(1, parsed));
    onPageChange(safePage);
    setGoToValue(String(safePage));
  };

  if (totalItems === 0 && !onPageSizeChange) {
    return null;
  }

  return (
    <div
      className={cn(
        "mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-2 text-sm",
        className
      )}
    >
      {onPageSizeChange ? (
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[72px] bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div />
      )}

      <div className="mx-auto flex items-center gap-2">
        <span className="text-foreground">Go to</span>
        <Input
          type="number"
          min={1}
          max={totalPages}
          value={goToValue}
          onChange={(e) => setGoToValue(e.target.value)}
          onBlur={handleGoToSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleGoToSubmit();
          }}
          className="h-8 w-14 rounded-full bg-muted px-3 text-center font-medium"
        />
        <span className="text-foreground">page</span>
      </div>

      <div className="ml-auto flex items-center">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="rounded-r-none"
        >
          Previous
        </Button>

        {pageNumbers.map((page, index) => {
          const prev = pageNumbers[index - 1];
          const showGap = prev !== undefined && page - prev > 1;
          return (
            <span key={page} className="flex items-center">
              {showGap ? (
                <span className="border-y border-border px-2 py-1.5 text-muted-foreground">
                  …
                </span>
              ) : null}
              <Button
                type="button"
                variant={page === currentPage ? "secondary" : "outline"}
                size="sm"
                onClick={() => onPageChange(page)}
                className="rounded-none border-x-0"
              >
                {page}
              </Button>
            </span>
          );
        })}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages || totalPages === 0}
          className="rounded-l-none"
        >
          Next
        </Button>
      </div>

      <p className="w-full text-xs text-muted-foreground">
        Showing <span className="font-semibold text-foreground">{start}</span> to{" "}
        <span className="font-semibold text-foreground">{end}</span> of{" "}
        <span className="font-semibold text-foreground">{totalItems}</span> records
      </p>
    </div>
  );
}

AppPagination.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  totalItems: PropTypes.number,
  pageSize: PropTypes.number,
  onPageSizeChange: PropTypes.func,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string,
};

AppPagination.defaultProps = {
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 10,
  onPageSizeChange: undefined,
  pageSizeOptions: [5, 10, 20, 50],
  className: "",
};
